import { useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState, useEffect } from 'react';
import { AISettingsContext, type AISettings, type AIProvider, type AISettingsContextType, type ProjectTemplate } from '@/contexts/AISettingsContext';
import { useFS } from '@/hooks/useFS';
import { readAISettings, writeAISettings } from '@/lib/configUtils';

interface AISettingsProviderProps {
  children: ReactNode;
}

export const DEFAULT_PROJECT_TEMPLATES: ProjectTemplate[] = [
  {
    id: 'mkstack',
    name: 'MKStack',
    description: 'Build Nostr clients with React. This is the default template that you should choose in the majority of cases. It ships with complete Nostr integration out of the box, enabling a variety of use-cases from social media to blogging to AI-powered apps. If you\'re not sure which template to choose, choose this one.',
    url: 'https://gitlab.com/soapbox-pub/mkstack.git',
  },
  {
    id: 'nutstack',
    name: 'Nutstack',
    description: 'This is a variant of MKStack extended to support Cashu integration. Choose this template only if the user has indicated they require Cashu integration.',
    url: 'https://gitlab.com/lemonknowsall/nutstack.git',
  },
  {
    id: 'luvstack',
    name: 'Luvstack',
    description: 'Barebones React template. Choose this to build mockups or demo sites that don\'t require Nostr functionality. Only choose this if the user has indicated they want to create a demo or mockup site, as it cannot support any sort of backend functionality or remote storage. It might also work for simple PWAs.',
    url: 'https://gitlab.com/soapbox-pub/luvstack.git',
  },
];

const DEFAULT_SETTINGS: AISettings = {
  providers: [],
  recentlyUsedModels: [],
  projectTemplates: DEFAULT_PROJECT_TEMPLATES,
};

export function AISettingsProvider({ children }: AISettingsProviderProps) {
  const queryClient = useQueryClient();
  const { fs } = useFS();
  const [settings, setSettings] = useState<AISettings>(DEFAULT_SETTINGS);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize settings from VFS on mount with optimizations
  useEffect(() => {
    let cancelled = false;

    const initializeSettings = async () => {
      try {
        const loadedSettings = await readAISettings(fs);

        // Check if component was unmounted during async operation
        if (cancelled) return;

        // Filter out recently used models that reference providers that no longer exist
        const validProviderIds = new Set(loadedSettings.providers.map(p => p.id));
        const validRecentlyUsedModels = loadedSettings.recentlyUsedModels.filter(modelId => {
          const providerId = modelId.split('/')[0];
          return validProviderIds.has(providerId);
        });

        // Migrate: If no project templates exist, use defaults
        const projectTemplates = loadedSettings.projectTemplates?.length > 0
          ? loadedSettings.projectTemplates
          : DEFAULT_PROJECT_TEMPLATES;

        setSettings({
          ...loadedSettings,
          recentlyUsedModels: validRecentlyUsedModels,
          projectTemplates,
        });
      } catch (error) {
        console.error('Failed to initialize AI settings:', error);
        if (!cancelled) {
          setSettings(DEFAULT_SETTINGS);
        }
      } finally {
        if (!cancelled) {
          setIsInitialized(true);
        }
      }
    };

    initializeSettings();

    return () => {
      cancelled = true;
    };
  }, [fs]);

  // Save settings to VFS whenever they change (but not during initialization)
  // Debounced to avoid excessive writes
  useEffect(() => {
    if (!isInitialized) return;

    const timeoutId = setTimeout(async () => {
      try {
        await writeAISettings(fs, settings);
      } catch (error) {
        console.error('Failed to save AI settings:', error);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [fs, settings, isInitialized]);

  const updateSettings = (newSettings: Partial<AISettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));

    // If providers are being updated, invalidate the provider-models query
    if ('providers' in newSettings) {
      queryClient.invalidateQueries({ queryKey: ['provider-models'] });
      queryClient.invalidateQueries({ queryKey: ['ai-credits'] });
    }
  };

  const setProvider = (provider: AIProvider) => {
    setSettings(prev => {
      const existingIndex = prev.providers.findIndex(p => p.id === provider.id);
      if (existingIndex >= 0) {
        // Update existing provider
        return {
          ...prev,
          providers: prev.providers.map(p =>
            p.id === provider.id ? provider : p
          ),
        };
      } else {
        // Add new provider
        return {
          ...prev,
          providers: [...prev.providers, provider],
        };
      }
    });
    queryClient.invalidateQueries({ queryKey: ['provider-models'] });
    queryClient.invalidateQueries({ queryKey: ['ai-credits'] });
  };

  const removeProvider = (id: string) => {
    setSettings(prev => ({
      ...prev,
      providers: prev.providers.filter(provider => provider.id !== id),
      // Also remove recently used models that reference this provider
      recentlyUsedModels: prev.recentlyUsedModels.filter(modelId => {
        const providerId = modelId.split('/')[0];
        return providerId !== id;
      }),
    }));
    queryClient.invalidateQueries({ queryKey: ['provider-models'] });
    queryClient.invalidateQueries({ queryKey: ['ai-credits'] });
  };

  const setProviders = (providers: AIProvider[]) => {
    setSettings(prev => ({
      ...prev,
      providers,
    }));
    queryClient.invalidateQueries({ queryKey: ['provider-models'] });
    queryClient.invalidateQueries({ queryKey: ['ai-credits'] });
  };

  const addRecentlyUsedModel = (modelId: string) => {
    setSettings(prev => {
      const existingIndex = prev.recentlyUsedModels.indexOf(modelId);
      if (existingIndex === 0) {
        return prev;
      } else if (existingIndex === -1) {
        // Add new model to front, keep only last 10
        return {
          ...prev,
          recentlyUsedModels: [modelId, ...prev.recentlyUsedModels].slice(0, 10),
        }
      } else {
        // Move existing model to front
        return {
          ...prev,
          recentlyUsedModels: [
            modelId,
            ...prev.recentlyUsedModels.filter((_, index) => index !== existingIndex)
          ]
        }
      }
    });
  };

  const addProjectTemplate = (template: ProjectTemplate) => {
    setSettings(prev => {
      // Check if template with this ID already exists
      if (prev.projectTemplates.some(t => t.id === template.id)) {
        return prev;
      }
      return {
        ...prev,
        projectTemplates: [...prev.projectTemplates, template],
      };
    });
  };

  const updateProjectTemplate = (template: ProjectTemplate) => {
    setSettings(prev => ({
      ...prev,
      projectTemplates: prev.projectTemplates.map(t =>
        t.id === template.id ? template : t
      ),
    }));
  };

  const removeProjectTemplate = (id: string) => {
    setSettings(prev => ({
      ...prev,
      projectTemplates: prev.projectTemplates.filter(t => t.id !== id),
    }));
  };

  const setProjectTemplates = (templates: ProjectTemplate[]) => {
    setSettings(prev => ({
      ...prev,
      projectTemplates: templates,
    }));
  };

  const isConfigured = settings.providers.length > 0;
  const isLoading = !isInitialized;

  const contextValue: AISettingsContextType = {
    settings,
    updateSettings,
    setProvider,
    removeProvider,
    setProviders,
    addRecentlyUsedModel,
    addProjectTemplate,
    updateProjectTemplate,
    removeProjectTemplate,
    setProjectTemplates,
    isConfigured,
    isLoading,
  };

  return (
    <AISettingsContext.Provider value={contextValue}>
      {children}
    </AISettingsContext.Provider>
  );
}